// ─────────────────────────────────────────────────────────────────────────────
// EMITIR — pantalla móvil de emisión de la GUÍA DE DESPACHO 52 (28-ago-2026).
//
// Desde noviembre queda SOLO para la guía. La factura y la nota de crédito se
// administran en el módulo Facturación (/facturacion): la factura se emite desde
// Producción → Por regularizar y las notas de crédito / anulación, en la pestaña
// Notas de Facturación. Si esta pantalla llega sin `?tipo=guia`, redirige allá.
//
// Para qué: emitir la guía desde el teléfono, parado al lado de la OT. Hoy emitir exige
// la pantalla de OTs completa, que en un celular se navega mal — y el momento de
// despachar es justamente cuando la pieza sale del taller, no cuando alguien vuelve al
// escritorio.
//
// El flujo, de arriba hacia abajo y sin ramas: número de OT → qué se puede despachar →
// cantidades y datos del traslado → previsualizar → emitir → PDF.
//
// NO REIMPLEMENTA NADA. Usa los endpoints que ya existen:
//   GET  /dte/emitible/:ot      qué queda por despachar (ítems con saldo)
//   GET  /dte/pre/guia/:ot      previsualización en PDF, ANTES de emitir
//   POST /guia                  guía de despacho 52 (con los datos de la Res. SII 154)
//   GET  /dte/emitidos/:ot      lo ya emitido de esa OT (para bajar el PDF)
//   GET  /dte/emitido{,-guia}/:folio/pdf
//
// Previsualizar va ANTES de emitir y está a un toque. Un DTE emitido no se borra: se
// anula con otra nota de crédito, con folio y con rastro en el SII. Mirar el PDF diez
// segundos antes es barato; una NC de corrección no lo es.
// ─────────────────────────────────────────────────────────────────────────────
import FormularioGuia, { faltantesGuia } from "./FormularioGuia";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import useTitle from "../../hooks/useTitle";

const C = {
  fondo: "#141414",
  borde: "#2a2a2a",
  campo: "#0f0f0f",
  texto: "#dfe4e8",
  tenue: "#8a8a8a",
  rojo: "#e74c3c",
  verde: "#2ecc71",
  ambar: "#f39c12",
  azul: "#3498db",
};

// Desde noviembre esta pantalla emite solo la guía de despacho 52.
const TIPOS = [
  { id: "guia", label: "Guía", dte: 52, icono: "🚚", folioTipo: "guia", disponible: true },
];

const clp = (v) => (v == null || isNaN(v) ? "—" : `$${Math.round(Number(v)).toLocaleString("es-CL")}`);

const caja = { background: C.fondo, border: `1px solid ${C.borde}`, borderRadius: 12, padding: 14, marginBottom: 12 };
const rotulo = { fontSize: 10, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 10 };
const inputBase = {
  background: C.campo,
  border: `1px solid ${C.borde}`,
  color: C.texto,
  borderRadius: 8,
  padding: "12px 14px",
  // 16px reales: bajo eso, iOS hace zoom al enfocar el campo y descoloca la pantalla.
  fontSize: 16,
  width: "100%",
};
const boton = (fondo, ancho = "100%") => ({
  background: fondo,
  border: "none",
  borderRadius: 10,
  color: "#fff",
  fontWeight: 700,
  fontSize: 15,
  // 46px: un dedo, no un mouse.
  padding: "13px 16px",
  width: ancho,
  cursor: "pointer",
});

const Emitir = () => {
  useTitle("Emitir");
  const auth = useContext(AuthContext);
  const roles = auth?.user?.roles || [];
  const puedeEntrar = roles.some((r) => ["admin", "operator", "accountant"].includes(r));

  // 26-oct: la pantalla se puede abrir precargada desde la OT (/emitir?ot=8435&tipo=guia).
  // El botón de guía de Órdenes de Trabajo llega por acá: es el único camino que exige
  // los datos de la Res. SII 154, y así la OT no se tipea dos veces.
  const [params] = useSearchParams();
  const otInicial = (params.get("ot") || "").trim();
  const navigate = useNavigate();
  const llegaComoGuia = params.get("tipo") === "guia";
  const [ot, setOt] = useState(otInicial);
  const [datos, setDatos] = useState(null);       // respuesta de /dte/emitible
  const [emitidos, setEmitidos] = useState([]);   // respuesta de /dte/emitidos
  const tipo = "guia";
  const [items, setItems] = useState([]);

  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(null);

  const tipoActual = TIPOS[0];

  // La factura y la nota de crédito ya no se emiten acá: se manda al módulo Facturación.
  useEffect(() => {
    if (puedeEntrar && !llegaComoGuia) {
      navigate("/facturacion", { replace: true, state: { aviso: "Esta pantalla ahora emite solo guías de despacho. Las facturas y notas de crédito se administran en Facturación." } });
    }
  }, [puedeEntrar, llegaComoGuia, navigate]);

  const buscar = useCallback(async () => {
    const numero = String(ot).trim();
    if (!numero) return;
    setCargando(true);
    setError("");
    setResultado(null);
    setDatos(null);
    setEmitidos([]);
    try {
      const d = (await backend.get(`dte/emitible/${numero}`)).data;
      setDatos(d);
      setItems((d.items || []).map((it) => ({ ...it, aEmitir: it.saldo })));
      // Lo ya emitido va en la misma búsqueda: es lo que permite bajar un PDF sin
      // volver a la pantalla de OTs, y elegir la factura de una NC.
      try {
        const e = (await backend.get(`dte/emitidos/${numero}`)).data;
        setEmitidos(e.documentos || []);
      } catch {
        // Que no se pueda listar lo emitido no puede impedir emitir algo nuevo.
        setEmitidos([]);
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setCargando(false);
    }
  }, [ot]);

  // Si vino con ?ot=, se busca sola al abrir (una vez).
  const otPrecargada = otInicial;
  useEffect(() => {
    if (otPrecargada) buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otPrecargada]);

  /**
   * Abre un PDF. La ventana se abre ANTES del await a propósito: Safari en iOS bloquea
   * `window.open` si no viene de un gesto directo, y con el await de por medio deja de
   * serlo (es el mismo patrón que ya usa la pantalla de OTs).
   */
  const abrirPdf = async (url, params = {}) => {
    const w = window.open("", "_blank");
    try {
      const res = await backend.get(url, { params, responseType: "blob" });
      const objeto = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (w) w.location = objeto;
      else window.location = objeto;
      setTimeout(() => window.URL.revokeObjectURL(objeto), 60000);
    } catch (e) {
      if (w) w.close();
      setError("No se pudo abrir el PDF: " + (e?.response?.data?.error || e.message));
    }
  };

  const previsualizar = () => {
    if (!datos) return;
    abrirPdf(`dte/pre/guia/${datos.wo.number}`, {
      tipoDoc: tipoActual.dte,
    });
  };

  // Datos legales de la guía. Arrancan con el origen y el momento actual; lo demás lo
  // pone quien despacha, porque cambia en cada retiro.
  const [guia, setGuia] = React.useState(() => ({
    tipoTraslado: "1",
    inicioTraslado: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
    destino: {}, transporte: {}, sinPrecio: { es: false }, trasladoProlongado: { es: false },
  }));
  const [tiposTraslado, setTiposTraslado] = React.useState([]);

  React.useEffect(() => {
    let vivo = true;
    backend.get("guia/opciones")
      .then(({ data }) => { if (vivo) setTiposTraslado(data.tiposTraslado || []); })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  const emitir = async () => {
    if (!datos || enviando) return;
    setEnviando(true);
    setError("");
    try {
      const aEmitir = items
        .filter((it) => Number(it.aEmitir) > 0)
        .map((it) => ({ piId: it.piId, qty: Number(it.aEmitir) }));
      if (!aEmitir.length) throw new Error("Ingresa las cantidades del documento");
      // La guía va por su propio endpoint, que además la guarda como documento
      // (POST /guia). Los campos de la Res. SII 154 recién bloquean el 1-nov-2026
      // (Res. 52 aplazó vigencia y sanciones): antes de eso se avisa y se deja
      // emitir. Frenar hoy un despacho por un requisito de noviembre sería peor que
      // el problema que resuelve — y la nómina de choferes de Rocmin todavía no
      // existe. El servidor vuelve a decidir lo mismo; esto sólo evita el viaje.
      const faltan = faltantesGuia(guia);
      if (faltan.length && !window.confirm(
        `A esta guía le faltan datos que la Res. SII 154 va a exigir desde el ` +
        `1-nov-2026: ${faltan.join(", ")}.\n\n¿Emitirla igual?`
      )) {
        setEnviando(false);
        return;
      }
      const r = await backend.post("guia", { woNumber: datos.wo.number, items: aEmitir, ...guia });
      setResultado(r.data);
      // Se refresca lo emitido: el documento recién salido tiene que aparecer en la
      // lista, con su PDF, sin obligar a buscar la OT de nuevo.
      try {
        const e = (await backend.get(`dte/emitidos/${datos.wo.number}`)).data;
        setEmitidos(e.documentos || []);
      } catch {
        /* la emisión ya ocurrió: no se toca el resultado por esto */
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setEnviando(false);
    }
  };

  if (!puedeEntrar) return <Navigate to="/dashboard" replace />;
  // Mientras redirige no se dibuja la pantalla.
  if (!llegaComoGuia) return null;

  return (
    <div style={{ padding: 14, maxWidth: 560, margin: "0 auto" }}>
      <h2 className="mv-screen-title" style={{ margin: "0 0 4px" }}>Emitir guía de despacho</h2>
      <p style={{ fontSize: 12, color: C.tenue, margin: "0 0 14px" }}>
        Desde el taller: busca la OT, revisa el borrador y emite la guía. Un DTE emitido no
        se borra — se corrige con una nota de crédito, así que conviene previsualizar.
      </p>

      {/* 1 · La OT */}
      <div style={caja}>
        <div style={rotulo}>1 · Orden de trabajo</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="number"
            inputMode="numeric"
            placeholder="N° de OT"
            value={ot}
            onChange={(e) => setOt(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && buscar()}
            style={{ ...inputBase, flex: 1 }}
          />
          <button onClick={buscar} disabled={cargando} style={boton(C.azul, "auto")}>
            {cargando ? "…" : "Buscar"}
          </button>
        </div>
        {datos && (
          <div style={{ marginTop: 10, fontSize: 13, color: C.texto }}>
            <div style={{ fontWeight: 700 }}>OT {datos.wo.number} · {datos.wo.name}</div>
            <div style={{ color: C.tenue, fontSize: 12 }}>
              {datos.receptor
                ? `${datos.receptor.name} · ${datos.receptor.rut}`
                : "⚠️ la OT no tiene cliente con RUT — complétalo en Contactos"}
              {datos.wo.clientOC ? ` · OC ${datos.wo.clientOC}` : ""}
            </div>
            {/* El ambiente se dice SIEMPRE: emitir creyendo que es certificación cuando
                es producción es un documento real en el SII. */}
            <div style={{ fontSize: 12, marginTop: 4, color: datos.esProduccion ? C.rojo : C.ambar, fontWeight: 700 }}>
              {datos.esProduccion ? "⬤ PRODUCCIÓN — el documento es real" : "○ certificación (ambiente de prueba)"}
            </div>
          </div>
        )}
      </div>

      {datos && (
        <>
          {/* 2 · Folio */}
          <div style={caja}>
            <div style={rotulo}>2 · Folio</div>
            {/* 26-oct: antes se mostraba max(folio)+1 de la colección factura (la serie de
                Enternet). El folio real lo asigna SimpleFactura desde el CAF cargado allá
                y no tiene por qué coincidir: se dice eso y no un número que después no es. */}
            <div style={{
              marginTop: 10, padding: "8px 10px", borderRadius: 7,
              background: C.campo, border: `1px solid ${C.borde}`, fontSize: 12, color: C.tenue,
            }}>
              Folio del DTE {tipoActual.dte}: lo asigna SimpleFactura al emitir (serie del CAF cargado allá); aparece en el resultado y en el PDF.
            </div>
          </div>

          {/* 3 · Detalle */}
          <div style={caja}>
            <div style={rotulo}>3 · Qué se despacha</div>

            {items.length === 0 ? (
              <div style={{ fontSize: 12, color: C.tenue }}>La OT no tiene ítems por emitir.</div>
            ) : (
              items.map((it, i) => (
                <div
                  key={it.piId}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: i ? `1px solid ${C.borde}` : "none" }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: C.texto, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {it.nombre}
                    </div>
                    <div style={{ fontSize: 11, color: C.tenue }}>
                      {it.sku ? `${it.sku} · ` : ""}saldo {it.saldo} de {it.qty} · {clp(it.precio)}
                    </div>
                  </div>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    max={it.saldo}
                    value={it.aEmitir}
                    onChange={(e) =>
                      setItems((prev) =>
                        prev.map((x, j) => (j === i ? { ...x, aEmitir: e.target.value } : x))
                      )
                    }
                    style={{ ...inputBase, width: 90, textAlign: "right", padding: "10px" }}
                  />
                </div>
              ))
            )}
          </div>

          {/* 3b · Los datos que la guía necesita por ley (Res. SII 154). */}
          {tipo === "guia" && (
            <FormularioGuia valor={guia} onCambio={setGuia} tiposTraslado={tiposTraslado} />
          )}

          {/* 4 · Acciones */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
            <button onClick={previsualizar} style={boton("#2c3e50")}>
              👁 Previsualizar antes de emitir
            </button>
            <button
              onClick={emitir}
              disabled={enviando}
              style={boton(enviando ? "#555" : datos.esProduccion ? C.rojo : C.verde)}
            >
              {enviando
                ? "Emitiendo…"
                : `Emitir ${tipoActual.label}${datos.esProduccion ? " (REAL)" : " (prueba)"}`}
            </button>
          </div>
        </>
      )}

      {error && (
        <div role="alert" style={{ ...caja, borderColor: "#5c1f1f", background: "#2d0f0f", color: "#f87171", fontSize: 13 }}>
          {String(error)}
        </div>
      )}

      {resultado && (
        <div style={{ ...caja, borderColor: "#1c5236", background: "#0d2a17" }}>
          <div style={{ ...rotulo, color: C.verde }}>Emitido</div>
          <div style={{ fontSize: 14, color: C.texto }}>
            Folio <strong>{resultado.folio ?? "—"}</strong>
            {resultado.tipoDoc ? ` · DTE ${resultado.tipoDoc}` : ""}
          </div>
          {resultado.estado && (
            <div style={{ fontSize: 12, color: C.tenue }}>estado SII: {resultado.estado}</div>
          )}
        </div>
      )}

      {/* 5 · Lo ya emitido de esta OT */}
      {emitidos.length > 0 && (
        <div style={caja}>
          <div style={rotulo}>Documentos de esta OT</div>
          {emitidos.map((d) => (
            <div
              key={d.id}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: `1px solid ${C.borde}` }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: C.texto }}>
                  DTE {d.tipoDoc} · folio {d.folio}
                  {d.anulada && <span style={{ color: C.rojo, fontSize: 11 }}> · anulada</span>}
                </div>
                <div style={{ fontSize: 11, color: C.tenue }}>
                  {d.fecha ? new Date(d.fecha).toLocaleDateString("es-CL") : ""} · {clp(d.total)}
                  {d.estadoSii ? ` · SII: ${d.estadoSii}` : ""}
                </div>
              </div>
              <button
                onClick={() =>
                  abrirPdf(
                    d.tipoDoc === 52 ? `dte/emitido-guia/${d.folio}/pdf` : `dte/emitido/${d.folio}/pdf`,
                    { tipo: d.tipoDoc }
                  )
                }
                style={{ ...boton("#2c3e50", "auto"), fontSize: 13, padding: "10px 14px" }}
              >
                PDF
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Emitir;
